import { useCallback, useLayoutEffect, useState, type RefObject } from 'react';

/**
 * Shared responsive placement algorithm for a portal-rendered panel anchored
 * to a trigger element: an anchored dropdown on desktop (flipping above the
 * trigger when room below is tight, and capping its height to whatever space
 * is actually available) collapsing to a full-width bottom sheet below the
 * mobile breakpoint. Extracted from `SearchPopover` so a second consumer
 * (`ReportRangeControl`'s panel) gets the same battle-tested algorithm
 * without a hand-rolled reimplementation - see
 * `.claude/rules/verify-load-bearing-claims.md` on extending a shared
 * abstraction rather than duplicating it. `SearchPopover` itself now calls
 * this hook too, so there is exactly one placement algorithm in the app.
 *
 * The caller owns the DOM: it renders the trigger and panel elements (a
 * search input, a preset list, whatever), attaches `triggerRef` and
 * `panelRef`, and reads the returned placement to lay the panel out. This
 * hook does no rendering of its own.
 *
 * **Returned object identity.** On the desktop (anchored) path, the main
 * measured branch of `update()` always calls `setPlacement` with a fresh
 * object literal, so the returned `ResponsivePanelPlacement` is a new
 * reference on most scroll/resize ticks. Two branches are identity-preserving
 * exceptions: the mobile path once `isMobile` is already true, and the
 * desktop `!triggerRef.current` early return once `prev.isMobile` is already
 * false (see the `update` callback below) - so a caller cannot rely on "always
 * fresh" holding for every tick. Any effect that must re-run only when
 * placement *meaningfully* changes (e.g. one that (re)registers a
 * document-level listener) should depend on the destructured primitives it
 * actually uses, never the returned object as a whole: putting the object
 * itself in a dependency array would re-register on every tick on desktop.
 */

/** Tailwind `sm` breakpoint. Below this the caller should render a bottom sheet. */
export const RESPONSIVE_PANEL_MOBILE_BREAKPOINT = 640;
/** Gap between trigger and an anchored panel. */
const GAP = 4;
/** Viewport-edge margin kept clear on every side. */
const MARGIN = 8;
/** Floor for the clamped panel height so a tight fit is still usable, not a sliver. */
const MIN_PANEL_HEIGHT = 140;

export interface ResponsivePanelPlacement {
	/** True below `RESPONSIVE_PANEL_MOBILE_BREAKPOINT` - render a bottom sheet instead of an anchored panel. */
	isMobile: boolean;
	/** Which side of the trigger the anchored panel sits on. Meaningless while `isMobile`. */
	placement: 'above' | 'below';
	/** Set when `placement === 'below'`; `'auto'` otherwise. */
	top: number | 'auto';
	/** Set when `placement === 'above'`; `'auto'` otherwise. */
	bottom: number | 'auto';
	left: number;
	/** Overlay-mode pinned width; 0 in 'below' mode (sizing comes from the caller's own width class). */
	width: number;
	/** Vertical space available on the chosen side; the caller should cap the panel's scrollable region to this. */
	maxHeight: number;
}

export interface UseResponsivePanelPlacementOptions {
	triggerRef: RefObject<HTMLElement | null>;
	/**
	 * The panel's own root element, measured for its natural width/height.
	 * **Only attached on the desktop (anchored) branch** - a caller rendering
	 * the mobile bottom sheet from the same `isMobile` flag typically has no
	 * equivalent ref to give it there, and the sheet's dimmed backdrop already
	 * owns dismissal. A consumer that also registers its own document-level
	 * outside-click listener against this ref MUST gate that listener behind
	 * `!isMobile`; otherwise the guard (`panelRef.current?.contains(target)`)
	 * fails open on mobile - `panelRef.current` is null there, so every click
	 * reads as "outside" and dismisses the panel on its own content.
	 */
	panelRef: RefObject<HTMLElement | null>;
	/**
	 * Preferred panel width in pixels for the anchored ('below') desktop mode,
	 * and the minimum width in 'overlay' mode (which will not shrink below the
	 * trigger's own width).
	 */
	widthPx: number;
	/**
	 * - 'below' (default): panel top sits below the trigger's bottom edge, both
	 *   visible. Right choice when the trigger is a compact element separate
	 *   from the panel (a chip, a button).
	 * - 'overlay': panel top aligns with the trigger's top, so the panel visually
	 *   replaces the trigger. Right choice for a full-width input-style trigger.
	 *
	 * Only sets the preferred (downward) anchor; the flip-above and mobile-sheet
	 * behaviour override it as needed.
	 */
	anchorMode?: 'below' | 'overlay';
	/**
	 * Horizontal alignment of the panel to the trigger: `'start'` (default) lines
	 * the left edges up, `'center'` centres the panel on the trigger (a panel
	 * wider than its trigger then overhangs both sides equally). Either way the
	 * panel is clamped inside the viewport.
	 */
	align?: 'start' | 'center';
	/** Space between the trigger and an anchored ('below') panel, in pixels. Defaults to 4. */
	gapPx?: number;
}

/** Calculates vertical placement while retaining each caller's gap and height policy. */
export function verticalPanelPlacement({ viewportHeight, downwardTop, upwardBottomEdge, margin, flipThreshold, minHeight, maxHeight = Infinity }: {
  viewportHeight: number;
  downwardTop: number;
  upwardBottomEdge: number;
  margin: number;
  flipThreshold: number;
  minHeight: number;
  maxHeight?: number;
}): { placement: 'above'; top: 'auto'; bottom: number; maxHeight: number } | { placement: 'below'; top: number; bottom: 'auto'; maxHeight: number } {
  const spaceBelow = viewportHeight - downwardTop - margin;
  const spaceAbove = upwardBottomEdge - margin;
  const above = spaceBelow < flipThreshold && spaceAbove > spaceBelow;
  const height = Math.max(minHeight, Math.min(maxHeight, above ? spaceAbove : spaceBelow));
  return above
    ? { placement: 'above', top: 'auto', bottom: viewportHeight - upwardBottomEdge, maxHeight: height }
    : { placement: 'below', top: downwardTop, bottom: 'auto', maxHeight: height };
}

export function useResponsivePanelPlacement({
	triggerRef,
	panelRef,
	widthPx,
	anchorMode = 'below',
	align = 'start',
	gapPx = GAP,
}: UseResponsivePanelPlacementOptions): ResponsivePanelPlacement {
	const [placement, setPlacement] = useState<ResponsivePanelPlacement>({
		isMobile: false,
		placement: 'below',
		top: 0,
		bottom: 'auto',
		left: 0,
		width: 0,
		maxHeight: MIN_PANEL_HEIGHT,
	});

	const update = useCallback(() => {
		const viewportWidth = window.innerWidth;
		const viewportHeight = window.innerHeight;

		// Below the breakpoint the caller renders a bottom sheet, which needs no anchoring.
		if (viewportWidth < RESPONSIVE_PANEL_MOBILE_BREAKPOINT) {
			// eslint-disable-next-line @eslint-react/set-state-in-effect -- layout measurement requires setState
			setPlacement((prev) => (prev.isMobile ? prev : { ...prev, isMobile: true }));
			return;
		}

		if (!triggerRef.current) {
			// eslint-disable-next-line @eslint-react/set-state-in-effect -- layout measurement requires setState
			setPlacement((prev) => (prev.isMobile ? { ...prev, isMobile: false } : prev));
			return;
		}
		const rect = triggerRef.current.getBoundingClientRect();

		// --- horizontal ---
		// In overlay mode the panel replaces the trigger visually, so it should be
		// the same width as the trigger or wider; never narrower (would jump
		// inwards when it opens). In below mode the panel sizes by widthPx.
		const overlayWidth = anchorMode === 'overlay' ? Math.max(rect.width, widthPx) : 0;
		const panelWidth = anchorMode === 'overlay' ? overlayWidth : (panelRef.current?.offsetWidth ?? widthPx);

		let left = align === 'center' ? rect.left + (rect.width - panelWidth) / 2 : rect.left;
		// If left-aligned to the trigger would overflow the right edge, prefer
		// right-aligning the panel to the trigger's right edge.
		if (align === 'start' && left + panelWidth > viewportWidth - MARGIN) {
			left = rect.right - panelWidth;
		}
		// Final clamp so the panel never spills past either edge of the viewport.
		left = Math.max(MARGIN, Math.min(left, viewportWidth - panelWidth - MARGIN));

		// --- vertical: flip above the trigger when room below is tight ---
		const downwardTop = anchorMode === 'overlay' ? rect.top : rect.bottom + gapPx;
		const vertical = verticalPanelPlacement({
      viewportHeight,
      downwardTop,
      upwardBottomEdge: anchorMode === 'overlay' ? rect.bottom : rect.top - gapPx,
      margin: MARGIN,
      flipThreshold: panelRef.current?.offsetHeight ?? 0,
      minHeight: MIN_PANEL_HEIGHT,
    });

		// eslint-disable-next-line @eslint-react/set-state-in-effect -- layout measurement requires setState
		setPlacement({
			isMobile: false,
			...vertical,
			left,
			width: overlayWidth,
		});
	}, [triggerRef, panelRef, widthPx, anchorMode, align, gapPx]);

	// useLayoutEffect fires synchronously after DOM insertion, before the browser
	// paints - so the panel is positioned correctly on the first frame with no flash.
	useLayoutEffect(() => {
		update();
		window.addEventListener('scroll', update, true);
		window.addEventListener('resize', update);
		return () => {
			window.removeEventListener('scroll', update, true);
			window.removeEventListener('resize', update);
		};
	}, [update]);

	return placement;
}
